// Distribution d'une animation : qui parle (Speaker 1, 2, 3).
//
// Monologue : un seul rôle, qu'une ou plusieurs personnes enregistrent (une
// voix par scène). Dialogue : 2 ou 3 rôles (« Le prof » / « L'élève », deux
// potes…) ; chaque scène est dite par UN rôle (`narration.speaker`), l'intro,
// l'accroche et le générique par Speaker 1. Chaque voix est enregistrée par qui
// tient le rôle, et l'avatar affiché est celui du compte qui a enregistré la
// voix (`speaker.ts`). Tout ici est PUR.

import { CAST_ROLE_IDS, type CastRoleId, type JrafCastRole } from './schema/document';

/** Ce que ces règles lisent d'un document (brut ou validé). */
interface DocLike {
  cast?: readonly unknown[] | null;
  scenes?: readonly unknown[] | null;
  assets?: readonly unknown[] | null;
  bookends?: Record<string, unknown> | null;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isRoleId(v: unknown): v is CastRoleId {
  return typeof v === 'string' && (CAST_ROLE_IDS as readonly string[]).includes(v);
}

/** Nom du rôle quand le document n'en donne pas. */
export const DEFAULT_ROLE_LABEL = 'Narrateur';

/** Distribution du document ; sans distribution : un narrateur (Speaker 1). */
export function castOf(doc: DocLike): JrafCastRole[] {
  const out: JrafCastRole[] = [];
  for (const r of doc.cast ?? [])
    if (isRecord(r) && isRoleId(r.id) && typeof r.label === 'string' && r.label.trim())
      if (!out.some((x) => x.id === r.id)) out.push({ id: r.id, label: r.label.trim() });
  return out.length ? out : [{ id: 's1', label: DEFAULT_ROLE_LABEL }];
}

/** Dialogue (2 ou 3 rôles) plutôt que monologue. */
export function isDialogue(doc: DocLike): boolean {
  return castOf(doc).length > 1;
}

/** « Speaker 2 » */
export function speakerNumberLabel(role: CastRoleId): string {
  return `Speaker ${role.slice(1)}`;
}

/** Rôle qui dit la narration d'une scène (Speaker 1 par défaut). */
export function sceneRole(doc: DocLike, sceneId: string): CastRoleId {
  const scene = (doc.scenes ?? []).find((s) => isRecord(s) && s.id === sceneId);
  const n = isRecord(scene) && isRecord(scene.narration) ? scene.narration : null;
  return n && isRoleId(n.speaker) ? n.speaker : 's1';
}

/** Nom d'un rôle de la distribution (« L'élève »). */
export function roleLabel(doc: DocLike, role: CastRoleId): string {
  return castOf(doc).find((r) => r.id === role)?.label ?? speakerNumberLabel(role);
}

/**
 * Rôles tenus par chaque compte qui a enregistré une voix utilisée
 * (générique : « Philippe — le prof »), dans l'ordre de la distribution.
 */
export function speakerRoles(doc: DocLike): Record<string, CastRoleId[]> {
  const byAsset = new Map<string, CastRoleId>();
  for (const s of doc.scenes ?? []) {
    if (!isRecord(s) || !isRecord(s.narration)) continue;
    const id = s.narration.audioAssetId;
    if (typeof id === 'string')
      byAsset.set(id, isRoleId(s.narration.speaker) ? s.narration.speaker : 's1');
  }
  for (const v of Object.values(doc.bookends ?? {}))
    if (typeof v === 'string' && !byAsset.has(v)) byAsset.set(v, 's1');
  const out: Record<string, CastRoleId[]> = {};
  for (const a of doc.assets ?? []) {
    if (!isRecord(a) || a.kind !== 'audio' || a.voice === 'synthetic') continue;
    const role = typeof a.id === 'string' ? byAsset.get(a.id) : undefined;
    const who = typeof a.speakerId === 'string' ? a.speakerId : null;
    if (!role || !who) continue;
    const list = (out[who] ??= []);
    if (!list.includes(role)) list.push(role);
  }
  for (const list of Object.values(out))
    list.sort((x, y) => CAST_ROLE_IDS.indexOf(x) - CAST_ROLE_IDS.indexOf(y));
  return out;
}

/**
 * Ce qu'écrit le générique sous l'avatar d'un compte en dialogue
 * (« le prof », « l'élève et Léa ») ; null en monologue.
 */
export function speakerRoleCaption(doc: DocLike, speakerId: string): string | null {
  if (!isDialogue(doc)) return null;
  const roles = speakerRoles(doc)[speakerId] ?? [];
  if (!roles.length) return null;
  const labels = roles.map((r) => roleLabel(doc, r));
  return labels.length === 1
    ? labels[0]!
    : `${labels.slice(0, -1).join(', ')} et ${labels[labels.length - 1]!}`;
}
